import {Injectable} from '@angular/core';
import {mostReadable, TinyColor} from "@ctrl/tinycolor";
import {Subject} from "rxjs";
import {OverlayContainer} from "@angular/cdk/overlay";
import {Community} from "../_types/communities";
import {LocalStorageService} from "./local-storage.service";
import {User} from "../_types/user";

export interface Color {
    name: string;
    hex: string;
    darkContrast: boolean;
}

enum currentBrightnessTheme {
    light = "light",
    dark = "dark",
    default = "default"
}

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    currentBrightnessTheme: Subject<currentBrightnessTheme> = new Subject<currentBrightnessTheme>();
    primaryColorPalette: Color[] = [];
    accentColorPalette: Color[] = [];
    private colorCache: Map<string, string> = new Map<string, string>();

    constructor(private _overlayContainer: OverlayContainer, private localStorageService: LocalStorageService) {
    }

    updateCurrentBrightnessTheme(theme?: currentBrightnessTheme): void {
        let community: Community | null = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);

        let user: User = JSON.parse(localStorage.getItem('currentUser') as string);
        if (community?.disable_dark_mode) {
            // if community has theming disabled then only allow light mode
            this.currentBrightnessTheme.next(currentBrightnessTheme.light);
            this.changeTheme(currentBrightnessTheme.light);
        } else if (theme && theme != currentBrightnessTheme.default) {
            // if its manually set then its being used from settings page so only temporary
            this.currentBrightnessTheme.next(theme);
            this.changeTheme(theme);
        } else {
            // no theme given
            if (theme == currentBrightnessTheme.default) {
                // if given default then ignore the user settings as its just temporary
                user.theme_mode = currentBrightnessTheme.default
            }

            if (user?.theme_mode == currentBrightnessTheme.dark) {
                this.currentBrightnessTheme.next(currentBrightnessTheme.dark);
                this.changeTheme(currentBrightnessTheme.dark);
            } else if (user?.theme_mode == currentBrightnessTheme.light) {
                this.currentBrightnessTheme.next(currentBrightnessTheme.light);
                this.changeTheme(currentBrightnessTheme.light);
            } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                this.currentBrightnessTheme.next(currentBrightnessTheme.dark);
                this.changeTheme(currentBrightnessTheme.dark);
            } else {
                this.currentBrightnessTheme.next(currentBrightnessTheme.light);
                this.changeTheme(currentBrightnessTheme.light);
            }
        }
    }

    changeTheme(theme: currentBrightnessTheme): void {

        const body: HTMLBodyElement = document.getElementsByTagName('body')[0]

        let community: Community | null = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
        if (theme == currentBrightnessTheme.dark) {
            if (!body.classList.contains('dark-mode')) {
                body.classList.add('dark-theme');
            }
            document.documentElement.style.setProperty(
                `--third-depth-card`, '#303134'
            );
            document.documentElement.style.setProperty(
                `--ck-color-base-background`, '#303134'
            );
            document.documentElement.style.setProperty(
                `--ck-color-button-default-hover-background`, '#6F6F6F'
            );
            document.documentElement.style.setProperty(
                `--ck-color-base-text`, 'white'
            );
            document.documentElement.style.setProperty(
                `--body-background-colour`, '#151618'
            );
            document.documentElement.style.setProperty(
                `--body-font-colour`, '#FFFFFF'
            );
            document.documentElement.style.setProperty(
                `--recent-activity-highlight-colour`, '#FFFFFF'
            );
            document.documentElement.style.setProperty(
                `--mat-tab-header-active-label-text-color`, '#FFFFFF'
            );
        } else {
            body.classList.remove('dark-theme');
            document.documentElement.style.setProperty(
                `--third-depth-card`, '#f0f0f0'
            );
            document.documentElement.style.setProperty(
                `--mdc-elevated-card-container-color`, 'white'
            );
            document.documentElement.style.setProperty(
                `--ck-color-base-background`, 'white'
            );
            document.documentElement.style.setProperty(
                `--ck-color-button-default-hover-background`, 'hsl(0, 0%, 94.1%)'
            );
            document.documentElement.style.setProperty(
                `--ck-color-base-text`, 'hsl(0, 0%, 20%)'
            );
            document.documentElement.style.setProperty(
                `--body-background-colour`, '#dfdfde'
            );

            if (community?.default_text_color_hex) {
                document.documentElement.style.setProperty(
                    `--body-font-colour`, community?.default_text_color_hex
                );
            } else {
                document.documentElement.style.setProperty(
                    `--body-font-colour`, '#585858'
                );
            }
            document.documentElement.style.setProperty(
                `--mat-tab-header-active-label-text-color`, 'var(--theme-accent-500)'
            );
        }

        // remove old theme class and add new theme class
        const overlayContainerClasses = this._overlayContainer?.getContainerElement().classList;
        if (overlayContainerClasses) {
            const themeClassesToRemove = Array.from(overlayContainerClasses)
                .filter((item: string) => item.includes('-theme'));
            if (themeClassesToRemove.length) {
                overlayContainerClasses.remove(...themeClassesToRemove);
            }
            if (theme === currentBrightnessTheme.dark) {
                overlayContainerClasses.add('dark-theme');
            } else {
                overlayContainerClasses.add('light-theme');
            }
        }
    }

    // Service to generate css variables from 2 colours for use in the theme.scss for angular themeing
    savePrimaryColor(color: string): void {
        this.primaryColorPalette = this.computeColors(color);
        document.documentElement.style.setProperty(
            `--primary`, color
        );
        let metaTheme = document.querySelector('meta[name="theme-color"]')
        if (metaTheme) {
            metaTheme.setAttribute('content', color)
        }
        let metaThemeTile = document.querySelector('meta[name="msapplication-TileColor"]')
        if (metaThemeTile) {
            metaThemeTile.setAttribute('content', color)
        }
        let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
        if (metaStatusBar) {
            metaStatusBar.setAttribute('content', color)
        }
        document.documentElement.style.setProperty(
            `--primary-text`, this.getReadableColor(color)
        );
        this.updateTheme(this.primaryColorPalette, 'primary');
    }

    saveAccentColor(color: string): void {
        this.accentColorPalette = this.computeColors(color);
        document.documentElement.style.setProperty(
            `--accent`, color
        );
        document.documentElement.style.setProperty(
            `--accent-text`, this.getReadableColor(color)
        );

        this.updateTheme(this.accentColorPalette, 'accent');
    }

    getReadableColor(color: string): string | null {
        if (!color) {
            return null;
        }

        if (this.colorCache.has(color)) {
            return this.colorCache.get(color)!;
        }

        const fallback: string = new TinyColor(color).isLight() ? 'rgba(black, 0.87)' : 'white';
        let mostReadableColor = mostReadable(
            color,
            ['#ffffff', '#000000', '#808080', '#C0C0C0'],
            {includeFallbackColors: true}
        )?.toHexString();
        if (!mostReadableColor) {
            mostReadableColor = fallback;
        }
        this.colorCache.set(color, mostReadableColor);

        return mostReadableColor;
    }


    updateTheme(colors: Color[], theme: string): void {
        colors.forEach(color => {
            document.documentElement.style.setProperty(
                `--theme-${theme}-${color.name}`,
                color.hex
            );
            document.documentElement.style.setProperty(
                `--theme-${theme}-contrast-${color.name}`,
                this.getReadableColor(color.hex)
            );
        });
    }

    computeColors(hex: string): Color[] {
        return [
            this.getColorObject(new TinyColor(hex).lighten(52), '50'),
            this.getColorObject(new TinyColor(hex).lighten(37), '100'),
            this.getColorObject(new TinyColor(hex).lighten(26), '200'),
            this.getColorObject(new TinyColor(hex).lighten(12), '300'),
            this.getColorObject(new TinyColor(hex).lighten(6), '400'),
            this.getColorObject(new TinyColor(hex), '500'),
            this.getColorObject(new TinyColor(hex).darken(6), '600'),
            this.getColorObject(new TinyColor(hex).darken(12), '700'),
            this.getColorObject(new TinyColor(hex).darken(18), '800'),
            this.getColorObject(new TinyColor(hex).darken(24), '900'),
            this.getColorObject(new TinyColor(hex).lighten(50).saturate(30), 'A100'),
            this.getColorObject(new TinyColor(hex).lighten(30).saturate(30), 'A200'),
            this.getColorObject(new TinyColor(hex).lighten(10).saturate(15), 'A400'),
            this.getColorObject(new TinyColor(hex).lighten(5).saturate(5), 'A700')
        ];
    }

    getColorObject(value: any, name: string): Color {
        const c: TinyColor = new TinyColor(value);
        return {
            name: name,
            hex: c.toHexString(),
            darkContrast: c.isLight()
        };
    }
}
